<script setup lang="ts">
import type { RefresherCustomEvent } from '@ionic/core';

defineExpose({
  open,
  close
});

const userConfigStore = useUserConfigurationStore();
const routingHistory = useRoutingHistoryStore();
const { messageNavigated } = storeToRefs(routingHistory);
const messageStore = useMessageStore();
const { openConfirmAlert } = useUserMessages();
const router = useRouting();
const { usingTouch } = useInputTracker();
const { t } = useEkI18n();

const isOpen = ref(false);
const loading = ref(true);

const searchSubheader = computed(() => {
  return messageStore._messages.length === 0
    ? t('messages.noMessagesReceived')
    : t('messages.noSearchResults');
});

function open() {
  isOpen.value = true;
  messageNavigated.value = '';
  messageStore.markAllAsRead(userConfigStore.fitterId);
}

function close() {
  messageStore.searchQuery = '';
  isOpen.value = false;
}

async function refresh(event?: RefresherCustomEvent) {
  loading.value = true;

  try {
    await messageStore.markAllAsRead(userConfigStore.fitterId);
    await messageStore.fetchMessages(userConfigStore.fitterId);
  } finally {
    loading.value = false;
    event?.detail.complete();
  }
}

async function openDeleteAlert(messageId: number) {
  const confirmed = await openConfirmAlert(t('alert.warning'), t('messages.deleteConfirmation'), t('buttons.delete'));

  if (confirmed) {
    await messageStore.deleteMessage(messageId);
  }
}

function goToWorksheet(message: FormattedMessage) {
  if (!message.WorksheetId || !message.Project?.Id) return;
  messageNavigated.value = useRouter().currentRoute.value.fullPath;
  router.replace(`/projects/${message?.Project?.Id}/worksheets/${message.WorksheetId}`, true);

  close();
}

</script>

<template>
  <Modal
    v-model:open="isOpen"
    animation-direction="vertical"
    :title="t('messages.title')"
    :close-override="close"
  >
    <template #subheader>
      <IonToolbar>
        <IonSearchbar
          v-model="messageStore.searchQuery"
          class="search-bar"
          :placeholder="t('search.placeholder')"
        />
      </IonToolbar>
    </template>

    <IonContent>
      <IonRefresher
        slot="fixed"
        :disabled="!usingTouch"
        @ion-refresh="refresh"
      >
        <IonRefresherContent />
      </IonRefresher>

      <IonList v-if="messageStore.searchResults.length > 0">
        <MessagesCard
          v-for="message in messageStore.searchResults"
          :key="message.Id"
          :message="message"
          @delete="openDeleteAlert(message.Id)"
          @go-to-worksheet="goToWorksheet(message)"
        />
      </IonList>

      <UserMessagesSearch
        v-else
        icon="fa-fw fa-light fa-envelope"
        :header="t('messages.noMessages')"
        :subheader="searchSubheader"
      />
    </IonContent>
  </Modal>
</template>

<style scoped>
.search-bar{
  --border-radius: 100px;
}
</style>
